import type { UtcTimezoneInfo } from '@/types/common';
import type { EventDetailResponse, EventDetail, EventTicket } from '@/types/event';
import dayjs from 'dayjs';

export const derivedEventSoldoutStatus = (status: string, end: UtcTimezoneInfo) => {
	// Derived final status on sold_out event
	if (
		status === EVENT_STATUS.PAYOUT_CREATED ||
		status === EVENT_STATUS.PAYOUT_WORKING ||
		status === EVENT_STATUS.PAYOUT_POSTPONED ||
		status === EVENT_STATUS.PAID_OUT
	) {
		return EVENT_STATUS.FINISHED;
	} else if (status !== EVENT_STATUS.SOLD_OUT) {
		return status;
	}
	const isPublish = dayjs().isBefore(end.utc);
	const isSoldOutBeforeFinished = status === EVENT_STATUS.SOLD_OUT && isPublish;

	if (isSoldOutBeforeFinished) {
		return EVENT_STATUS.OPEN;
	}
	return EVENT_STATUS.FINISHED;
};

export const transformEventDetail = (data: EventDetailResponse): EventDetail => {
	const coordinateArray: String[] | null = data.locationSettings.venueGeoCoordinates ? data.locationSettings.venueGeoCoordinates.split(',') : null;
	const coordinate = coordinateArray ? {
		lat: Number(coordinateArray[0]),
		lng: Number(coordinateArray[1]),
	} : null;

	const tickets = data.tickets
		.filter((ticket) => ticket.status !== EVENT_STATUS.DELETED)
		.map((ticket): EventTicket => {
			return {
				id: ticket.id ?? null,
				name: ticket.name,
				quantity: ticket.seatsMax,
				soldQuantity: ticket.seatsSold ?? null,
				currency: ticket.currency ?? null,
				price: ticket.price,
				status: ticket.status,
				promocodes: ticket.promocodes ?? [],
				requiresTokenGates: ticket.requiresTokenGates,
			};
		});

	const status = derivedEventSoldoutStatus(data.status, data.end);
	const isSoldout = data.status === EVENT_STATUS.SOLD_OUT;

	return {
		id: data.id,
		status,
		title: data.name,
		description: data.details.description,
		startDateTime: data.start.utc,
		endDateTime: data.end.utc,
		timezone: data.start.timezone,
		publishedDateTime: data.published?.utc,
		publishedTimezone: data.published?.timezone,
		locationType: data.locationType,
		location: {
			name: data.locationSettings.venueName,
			address: data.locationSettings.venueAddress,
			coordinate,
		},
		url: data.streamingSettings?.url ?? '',
		instruction: data.streamingSettings?.attendeeInfo ?? '',
		coverUrl: data.details.coverUrl ?? '',
		tickets,
		organizerId: data.organizerInfo.userId,
		organizerGroupId: data.groupId,
		tokenGates: {
			contractAddress: data.tokenGates?.contract_address,
			chainId: data.tokenGates?.chain_id,
		},
		tokenGatingOptions: data.tokenGatingOptions,
		hasAiCoverImage: data.miscSettings.hasAiCoverImage,
		isSoldout,
		countryId: data.countryId,
		externalTicketingUrl: data.externalTicketingUrl,
		listingOptions: {
			isRestrictedEventType: data.listingOptions.isRestrictedEventType,
			isSelection: data.listingOptions.isSelection,
			showAttendeesName: data.listingOptions.showAttendeesName,
			showAttendeesCount: data.listingOptions.showAttendeesCount,
		},
	};
};

export const transformEventDescription = (description: string) => {
	if (!description) {
		return '';
	}
	const chunks: string[] = [];
	description.split('\n').forEach((word: string) => {
		if (word.trim() === '') {
			chunks.push('<br>');
		} else if (word.match(REGEX_LINK)) {
			// if find URL link pattern, then return anchor link instead
			let linkContent = '';
			word.split(' ').forEach((w: string) => {
				if (w.match(REGEX_LINK)) {
					linkContent += `<a href="${w}" target="_blank">${w}</a>`;
				} else {
					linkContent += `<span>${w}</span>`;
				}
				linkContent += ' ';
			});
			chunks.push(`<p>${linkContent}</p>`);
		} else {
			chunks.push(`<p>${word}</p>`);
		}
	});
	return chunks.join(' ');
};